import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FooterClicksTab } from "@/components/FooterClicksTab";
import { ContentViewsTab } from "@/components/ContentViewsTab";
import { SpecialBetsAdmin } from "@/components/SpecialBetsAdmin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Trash2, Upload, Pencil, ChevronLeft, ChevronRight, Bell } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { phaseLabels, phaseOrder, formatKickoff, toLocalInput } from "@/lib/utils-app";
import { parseFixturesFile, REQUIRED_COLS, OPTIONAL_COLS, RECOMMENDED_COLS, ALL_COLS, VALID_PHASES, VALID_STATUSES, buildCsvTemplate, normalizePhase, normalizeStatus } from "@/lib/csv";
import { Download, Check } from "lucide-react";
import { APP_THEMES, AppTheme, getTheme, setTheme, fetchDefaultTheme, setDefaultTheme } from "@/lib/theme";
import { Star } from "lucide-react";
import { PushDeliveryLogTab } from "@/components/PushDeliveryLogTab";
import { InfoBannerAdmin } from "@/components/InfoBannerAdmin";
import { NewsletterCampaignsTab } from "@/components/NewsletterCampaignsTab";

interface Tournament {
  id: string; name: string; year: number; slug: string; is_active: boolean;
  name_cs?: string | null; name_en?: string | null;
  description?: string | null; description_cs?: string | null; description_en?: string | null;
  url?: string | null; logo_url?: string | null; icon_url?: string | null;
}
interface Match {
  id: string; tournament_id: string; phase: string; group_name: string | null;
  kickoff_at: string; home_team: string; away_team: string;
  home_score: number | null; away_score: number | null; status: string;
  suppress_result_email?: boolean | null;
}

const slugify = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const ICON_EMOJIS = ["🏆","⚽","🏀","🏈","⚾","🎾","🏐","🏉","🏒","🏓","🏸","🥊","🥋","⛳","🏎️","🚴","🎯","🥇","🔥","⭐","🌍","🎮","🎲","👑"];

const isUrl = (s?: string | null) => !!s && /^https?:\/\//i.test(s.trim());

const AdminPage = () => {
  const { t } = useTranslation();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<string>("");

  const load = async () => {
    const [{ data: ts }, { data: ms }] = await Promise.all([
      supabase.from("tournaments").select("*").order("year", { ascending: false }),
      supabase.from("matches").select("*").order("kickoff_at"),
    ]);
    setTournaments((ts as Tournament[]) ?? []);
    setMatches((ms as Match[]) ?? []);
    if (!selectedTournament && ts && ts.length > 0) setSelectedTournament(ts[0].id);
  };
  useEffect(() => { load(); }, []);

  return (
    <AppShell title={t("admin.title")}>
      <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}
      </Link>
      <h1 className="text-2xl sm:text-3xl font-bold mb-1">{t("admin.title")}</h1>
      <p className="text-muted-foreground text-sm mb-5">{t("admin.subtitle")}</p>

      <Tabs defaultValue="tournaments">
        <TabsList className="w-full overflow-x-auto justify-start no-scrollbar">
          <TabsTrigger value="tournaments">{t("admin.tabs.tournaments")}</TabsTrigger>
          <TabsTrigger value="matches">{t("admin.tabs.matches")}</TabsTrigger>
          <TabsTrigger value="results">{t("admin.tabs.results")}</TabsTrigger>
          <TabsTrigger value="groupResults">Skupiny</TabsTrigger>
          <TabsTrigger value="specialBets">Speciální tipy</TabsTrigger>
          <TabsTrigger value="import">{t("admin.tabs.import")}</TabsTrigger>
          <TabsTrigger value="appearance">{t("admin.tabs.appearance")}</TabsTrigger>
          <TabsTrigger value="users">Uživatelé</TabsTrigger>
          <TabsTrigger value="pushlog">Push log</TabsTrigger>
          <TabsTrigger value="infobanner">Info panel</TabsTrigger>
          <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
          <TabsTrigger value="footerclicks">Patička</TabsTrigger>
          <TabsTrigger value="contentviews">Návštěvy stránek</TabsTrigger>
        </TabsList>

        <TabsContent value="tournaments" className="pt-5">
          <TournamentsSection tournaments={tournaments} onChange={load} />
        </TabsContent>
        <TabsContent value="matches" className="pt-5">
          <MatchesSection tournaments={tournaments} matches={matches}
            selected={selectedTournament} setSelected={setSelectedTournament} onChange={load} />
        </TabsContent>
        <TabsContent value="results" className="pt-5">
          <ResultsSection tournaments={tournaments} matches={matches}
            selected={selectedTournament} setSelected={setSelectedTournament} onChange={load} />
        </TabsContent>
        <TabsContent value="groupResults" className="pt-5">
          <GroupResultsSection tournaments={tournaments} matches={matches}
            selected={selectedTournament} setSelected={setSelectedTournament} />
        </TabsContent>
        <TabsContent value="specialBets" className="pt-5">
          <SpecialBetsAdmin tournaments={tournaments} matches={matches}
            selected={selectedTournament} setSelected={setSelectedTournament} />
        </TabsContent>
        <TabsContent value="import" className="pt-5">
          <ImportSection tournaments={tournaments} selected={selectedTournament}
            setSelected={setSelectedTournament} onChange={load} />
        </TabsContent>
        <TabsContent value="appearance" className="pt-5">
          <AppearanceSection />
        </TabsContent>
        <TabsContent value="users" className="pt-5">
          <UsersSection />
        </TabsContent>
        <TabsContent value="pushlog" className="pt-5">
          <PushDeliveryLogTab />
        </TabsContent>
        <TabsContent value="footerclicks" className="pt-5">
          <FooterClicksTab />
        </TabsContent>
        <TabsContent value="contentviews" className="pt-5">
          <ContentViewsTab />
        </TabsContent>
        <TabsContent value="newsletter" className="pt-5">
          <NewsletterCampaignsTab />
        </TabsContent>
        <TabsContent value="infobanner" className="pt-5">
          <InfoBannerAdmin />
        </TabsContent>
      </Tabs>
    </AppShell>
  );
};


const PAGE_SIZES = [10, 25, 50];

interface UserLeagueRow {
  league_id: string;
  league_name: string;
  role: string;
  is_owner: boolean;
  joined_at: string;
}

const UserLeaguesSection = ({ userId, open }: { userId: string; open: boolean }) => {
  const [leagues, setLeagues] = useState<UserLeagueRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase.rpc("admin_list_user_leagues", { _user_id: userId });
      if (error) toast.error(error.message);
      setLeagues((data as any[]) ?? []);
      setLoading(false);
    })();
  }, [userId, open]);

  return (
    <div className="space-y-2">
      <div className="text-sm font-medium">Ligy</div>
      {loading ? (
        <p className="text-xs text-muted-foreground">Načítám…</p>
      ) : leagues.length === 0 ? (
        <p className="text-xs text-muted-foreground">Uživatel není v žádné lize.</p>
      ) : (
        <div className="divide-y border rounded-md max-h-60 overflow-y-auto">
          {leagues.map((ul) => (
            <div key={ul.league_id} className="flex items-center justify-between gap-3 p-2 text-sm">
              <span className="truncate">{ul.league_name}</span>
              <Badge variant={ul.is_owner ? "default" : "secondary"} className="text-[10px]">
                {ul.is_owner ? "Vlastník" : ul.role === "admin" ? "Admin" : "Člen"}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const UserDetailDialog = ({ user, onPremiumChange }: {
  user: { id: string; name: string | null; is_premium: boolean };
  onPremiumChange: (v: boolean) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { isSuperAdmin } = useAuth();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">Detail</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{user.name || "Uživatel"}</DialogTitle>
          <DialogDescription>Správa uživatele (super admin)</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          {isSuperAdmin && <UserLeaguesSection userId={user.id} open={open} />}
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Premium</div>
              <div className="text-xs text-muted-foreground">Přiřadit / odebrat roli premium</div>
            </div>
            <Switch
              checked={user.is_premium}
              onCheckedChange={async (v) => {
                const { error } = await supabase.rpc("admin_set_premium", { _user_id: user.id, _premium: v });
                if (error) { toast.error(error.message); return; }
                onPremiumChange(v);
                toast.success(v ? "Premium přiděleno" : "Premium odebráno");
              }}
            />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <div className="text-sm font-medium">Testovací notifikace</div>
              <div className="text-xs text-muted-foreground">Odeslat všechny typy push notifikací</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const { error } = await supabase.rpc("admin_send_test_push", { _user_id: user.id });
                if (error) { toast.error(error.message); return; }
                toast.success("Testovací push odeslán");
              }}
            >
              <Bell className="h-4 w-4 mr-1" /> Odeslat
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const UsersSection = () => {
  const [rows, setRows] = useState<Array<{ id: string; name: string | null; avatar_url: string | null; created_at: string; owned_leagues: number; is_premium: boolean }>>([]);

  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("leagues_desc");

  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(0); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const searchArg = search || undefined;
      const [{ data: rowsData, error: rowsErr }, { data: countData, error: countErr }] = await Promise.all([
        supabase.rpc("admin_list_users_with_league_counts", {
          _page_size: pageSize,
          _page_offset: page * pageSize,
          _search: searchArg,
          _sort_by: sortBy,
        }),
        supabase.rpc("admin_count_users", { _search: searchArg }),
      ]);
      if (rowsErr) toast.error(rowsErr.message);
      if (countErr) toast.error(countErr.message);
      setRows((rowsData as any[])?.map((r) => ({ ...r, owned_leagues: Number(r.owned_leagues), is_premium: !!r.is_premium })) ?? []);

      setTotal(countData ?? 0);
      setLoading(false);
    })();
  }, [page, pageSize, search, sortBy]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const startItem = total === 0 ? 0 : page * pageSize + 1;
  const endItem = Math.min((page + 1) * pageSize, total);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Seznam uživatelů</CardTitle>
        <CardDescription>
          Celkem {total} uživatelů
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <Input
            placeholder="Hledat podle jména…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10 max-w-sm"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Po stránce:</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-10 w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => (
                  <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Seřadit:</span>
            <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
              <SelectTrigger className="h-10 w-[190px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="leagues_desc">Počet lig ↓</SelectItem>
                <SelectItem value="leagues_asc">Počet lig ↑</SelectItem>
                <SelectItem value="registered_desc">Registrace ↓</SelectItem>
                <SelectItem value="registered_asc">Registrace ↑</SelectItem>
                <SelectItem value="name_asc">Jméno A-Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Načítám…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Žádní uživatelé.</p>
        ) : (
          <>
            <div className="divide-y border rounded-lg">
              {rows.map((r) => (
                <div key={r.id} className="flex items-center gap-3 p-3">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-border" />
                  ) : (
                    <span className="h-9 w-9 rounded-full bg-muted flex items-center justify-center text-sm font-semibold">
                      {(r.name ?? "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate">{r.name || "—"}</span>
                      {r.is_premium && (
                        <Badge className="border-0 bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-md text-[10px] px-2 py-0">Premium</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge variant={r.owned_leagues > 0 ? "default" : "secondary"}>
                    {r.owned_leagues} {r.owned_leagues === 1 ? "liga" : r.owned_leagues >= 2 && r.owned_leagues <= 4 ? "ligy" : "lig"}
                  </Badge>
                  <div className="flex items-center gap-2 ml-2">
                    <UserDetailDialog user={r} onPremiumChange={(v) => setRows((curr) => curr.map((x) => x.id === r.id ? { ...x, is_premium: v } : x))} />
                  </div>

                </div>
              ))}

            </div>

            <div className="flex items-center justify-between pt-1">
              <span className="text-sm text-muted-foreground">
                {startItem}–{endItem} z {total}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Předchozí
                </Button>
                <span className="text-sm font-medium min-w-[4rem] text-center">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || loading}
                >
                  Další <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const AppearanceSection = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<AppTheme>(getTheme());
  const [defaultTheme, setDefaultThemeState] = useState<AppTheme>("default");
  const [savingDefault, setSavingDefault] = useState<AppTheme | null>(null);

  useEffect(() => {
    fetchDefaultTheme().then(setDefaultThemeState);
  }, []);

  const choose = (id: AppTheme) => {
    setTheme(id);
    setCurrent(id);
    toast.success(t("admin.appearance.applied"));
  };

  const makeDefault = async (id: AppTheme) => {
    setSavingDefault(id);
    try {
      await setDefaultTheme(id);
      setDefaultThemeState(id);
      toast.success(t("admin.appearance.defaultSaved"));
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setSavingDefault(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("admin.appearance.title")}</CardTitle>
        <CardDescription>{t("admin.appearance.desc")}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {APP_THEMES.map((th) => {
          const active = current === th.id;
          const isDefault = defaultTheme === th.id;
          const swatches =
            th.id === "emerald"
              ? ["hsl(0 0% 100%)", "hsl(145 70% 35%)", "hsl(145 75% 40%)"]
              : th.id === "greenblack"
              ? ["hsl(145 80% 42%)", "hsl(150 30% 10%)", "hsl(145 85% 55%)"]
              : ["hsl(265 60% 22%)", "hsl(338 85% 56%)", "hsl(320 90% 65%)"];
          return (
            <div
              key={th.id}
              className={`rounded-2xl border p-4 transition ${
                active ? "border-primary bg-primary/5 shadow-card" : "border-border"
              }`}
            >
              <button
                type="button"
                onClick={() => choose(th.id)}
                className="w-full text-left"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold flex items-center gap-2">
                    {t(th.labelKey)}
                    {isDefault && (
                      <Badge variant="secondary" className="gap-1">
                        <Star className="h-3 w-3" />
                        {t("admin.appearance.default")}
                      </Badge>
                    )}
                  </span>
                  {active && <Check className="h-4 w-4 text-primary" />}
                </div>
                <div className="flex gap-2">
                  {swatches.map((c, i) => (
                    <span key={i} className="h-8 w-8 rounded-full border border-border" style={{ background: c }} />
                  ))}
                </div>
              </button>
              <div className="mt-3 flex justify-end">
                <Button
                  variant={isDefault ? "secondary" : "outline"}
                  size="sm"
                  disabled={isDefault || savingDefault === th.id}
                  onClick={() => makeDefault(th.id)}
                >
                  <Star className="h-3.5 w-3.5 mr-1.5" />
                  {isDefault ? t("admin.appearance.isDefault") : t("admin.appearance.setDefault")}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const TournamentsSection = ({ tournaments, onChange }: { tournaments: Tournament[]; onChange: () => void }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [editing, setEditing] = useState<Tournament | null>(null);

  const create = async () => {
    if (!name.trim()) return;
    const slug = slugify(name) + "-" + year;
    const { error } = await supabase.from("tournaments").insert({ name: name.trim(), year, slug, is_active: true });
    if (error) toast.error(error.message); else { toast.success(t("admin.saved")); setName(""); onChange(); }
  };
  const toggleActive = async (id: string, v: boolean) => {
    const { error } = await supabase.from("tournaments").update({ is_active: v }).eq("id", id);
    if (error) toast.error(error.message); else onChange();
  };
  const remove = async (id: string) => {
    if (!confirm(t("admin.confirmDelete"))) return;
    const { error } = await supabase.from("tournaments").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success(t("admin.deleted")); onChange(); }
  };
  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.slug.trim()) return toast.error(t("admin.invalidData"));
    const { error } = await supabase.from("tournaments").update({
      name: editing.name.trim(),
      name_cs: editing.name_cs?.trim() || null,
      name_en: editing.name_en?.trim() || null,
      slug: slugify(editing.slug),
      year: editing.year,
      description_cs: editing.description_cs?.trim() || null,
      description_en: editing.description_en?.trim() || null,
      url: editing.url?.trim() || null,
      logo_url: editing.logo_url?.trim() || null,
      icon_url: editing.icon_url?.trim() || null,
    }).eq("id", editing.id);
    if (error) toast.error(error.message); else { toast.success(t("admin.saved")); setEditing(null); onChange(); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">{t("admin.newTournament")}</CardTitle></CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-[1fr,120px,auto]">
          <div className="space-y-1"><Label>{t("admin.name")}</Label><Input value={name} onChange={e => setName(e.target.value)} className="h-11" /></div>
          <div className="space-y-1"><Label>{t("admin.year")}</Label><Input type="number" value={year} onChange={e => setYear(parseInt(e.target.value) || year)} className="h-11" /></div>
          <div className="flex items-end"><Button onClick={create} className="h-11 w-full"><Plus className="h-4 w-4 mr-1" />{t("admin.create")}</Button></div>
        </CardContent>
      </Card>

      {editing && (
        <Card className="border-primary/40">
          <CardHeader><CardTitle className="text-base">{t("admin.editTournament")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-[1fr,120px]">
              <div className="space-y-1"><Label>{t("admin.name")}</Label>
                <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="h-11" /></div>
              <div className="space-y-1"><Label>{t("admin.year")}</Label>
                <Input type="number" value={editing.year} onChange={e => setEditing({ ...editing, year: parseInt(e.target.value) || editing.year })} className="h-11" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label>{t("admin.nameCs")}</Label>
                <Input value={editing.name_cs ?? ""} onChange={e => setEditing({ ...editing, name_cs: e.target.value })} className="h-11" /></div>
              <div className="space-y-1"><Label>{t("admin.nameEn")}</Label>
                <Input value={editing.name_en ?? ""} onChange={e => setEditing({ ...editing, name_en: e.target.value })} className="h-11" /></div>
            </div>
            <div className="space-y-1"><Label>{t("admin.slug")}</Label>
              <Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} className="h-11 font-mono" /></div>
            <div className="space-y-1"><Label>{t("admin.descriptionCs")}</Label>
              <textarea value={editing.description_cs ?? ""} onChange={e => setEditing({ ...editing, description_cs: e.target.value })}
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
            <div className="space-y-1"><Label>{t("admin.descriptionEn")}</Label>
              <textarea value={editing.description_en ?? ""} onChange={e => setEditing({ ...editing, description_en: e.target.value })}
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
            <div className="space-y-1"><Label>{t("admin.url")}</Label>
              <Input value={editing.url ?? ""} onChange={e => setEditing({ ...editing, url: e.target.value })} className="h-11" placeholder="https://..." /></div>
            <div className="space-y-1">
              <Label>{t("admin.logoUrl")}</Label>
              <Input value={editing.logo_url ?? ""} onChange={e => setEditing({ ...editing, logo_url: e.target.value })} className="h-11" placeholder="https://..." />
              {editing.logo_url && <img src={editing.logo_url} alt="logo" className="mt-1 h-12 object-contain" />}
            </div>
            {!editing.logo_url?.trim() && (
              <div className="space-y-1">
                <Label>{t("admin.icon")}</Label>
                <p className="text-xs text-muted-foreground">{t("admin.iconHelp")}</p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {ICON_EMOJIS.map(e => {
                    const active = editing.icon_url === e;
                    return (
                      <button key={e} type="button"
                        onClick={() => setEditing({ ...editing, icon_url: active ? null : e })}
                        className={`h-10 w-10 rounded-md border text-xl flex items-center justify-center transition ${active ? "border-primary bg-primary/10" : "border-input hover:bg-accent"}`}>
                        {e}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={saveEdit} className="bg-gradient-pitch">{t("common.save")}</Button>
              <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {tournaments.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noTournaments")}</p>}
        {tournaments.map(tr => (
          <Card key={tr.id}>
            <CardContent className="flex items-center gap-3 p-4">
              {tr.logo_url
                ? <img src={tr.logo_url} alt="" className="h-8 w-8 object-contain shrink-0" />
                : tr.icon_url
                  ? (isUrl(tr.icon_url)
                      ? <img src={tr.icon_url} alt="" className="h-8 w-8 object-contain shrink-0" />
                      : <span className="text-2xl shrink-0">{tr.icon_url}</span>)
                  : null}
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{tr.name} <span className="text-muted-foreground font-normal">· {tr.year}</span></div>
                <div className="text-xs text-muted-foreground font-mono">{tr.slug}</div>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{t("admin.active")}</span>
                <Switch checked={tr.is_active} onCheckedChange={(v) => toggleActive(tr.id, v)} />
              </div>
              <Button variant="ghost" size="icon" onClick={() => setEditing(tr)}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(tr.id)}><Trash2 className="h-4 w-4" /></Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const TournamentPicker = ({ tournaments, value, onChange }: any) => {
  const { t } = useTranslation();
  return (
    <div className="mb-4">
      <Label className="text-xs text-muted-foreground">{t("admin.tournament")}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-11"><SelectValue placeholder={t("admin.pickTournament")} /></SelectTrigger>
        <SelectContent>{tournaments.map((tr: Tournament) =>
          <SelectItem key={tr.id} value={tr.id}>{tr.name} · {tr.year}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

const emptyMatch = (tid: string) => ({
  tournament_id: tid, phase: "group", group_name: "",
  kickoff_at: toLocalInput(new Date().toISOString()),
  home_team: "", away_team: "",
});

const MatchesSection = ({ tournaments, matches, selected, setSelected, onChange }: any) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<any>(null);
  const filtered = useMemo(() => matches.filter((m: Match) => m.tournament_id === selected), [matches, selected]);

  const startNew = () => setEditing(emptyMatch(selected));
  const save = async () => {
    if (!editing.home_team || !editing.away_team || !editing.kickoff_at) return toast.error(t("admin.invalidData"));
    const payload = {
      tournament_id: editing.tournament_id,
      phase: editing.phase as any,
      group_name: editing.group_name || null,
      kickoff_at: new Date(editing.kickoff_at).toISOString(),
      home_team: editing.home_team.trim(),
      away_team: editing.away_team.trim(),
    };
    const { error } = editing.id
      ? await supabase.from("matches").update(payload).eq("id", editing.id)
      : await supabase.from("matches").insert(payload);
    if (error) toast.error(error.message); else { toast.success(t("admin.saved")); setEditing(null); onChange(); }
  };

  const remove = async (id: string) => {
    if (!confirm(t("admin.confirmDelete"))) return;
    const { error } = await supabase.from("matches").delete().eq("id", id);
    if (error) toast.error(error.message); else { toast.success(t("admin.deleted")); onChange(); }
  };

  const renderEditForm = () => (
    <CardContent className="space-y-3 border-t pt-4 bg-muted/30">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label>{t("admin.phase")}</Label>
          <Select value={editing.phase} onValueChange={(v) => setEditing({ ...editing, phase: v })}>
            <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{phaseOrder.map(p => <SelectItem key={p} value={p}>{phaseLabels[p]}</SelectItem>)}</SelectContent>
          </Select></div>
        <div className="space-y-1"><Label>{t("admin.group")}</Label>
          <Input value={editing.group_name ?? ""} onChange={e => setEditing({ ...editing, group_name: e.target.value })} className="h-11" placeholder="A" /></div>
      </div>
      <div className="space-y-1"><Label>{t("admin.kickoff")}</Label>
        <Input type="datetime-local" value={editing.kickoff_at ?? ""} onChange={e => setEditing({ ...editing, kickoff_at: e.target.value })} className="h-11" /></div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label>{t("admin.home")}</Label><Input value={editing.home_team} onChange={e => setEditing({ ...editing, home_team: e.target.value })} className="h-11" /></div>
        <div className="space-y-1"><Label>{t("admin.away")}</Label><Input value={editing.away_team} onChange={e => setEditing({ ...editing, away_team: e.target.value })} className="h-11" /></div>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} className="bg-gradient-pitch">{t("common.save")}</Button>
        <Button variant="outline" onClick={() => setEditing(null)}>{t("common.cancel")}</Button>
      </div>
    </CardContent>
  );

  return (
    <div>
      <TournamentPicker tournaments={tournaments} value={selected} onChange={setSelected} />
      <div className="mb-3 flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{filtered.length} {t("league.matchesCount")}</p>
        <Button size="sm" onClick={startNew} disabled={!selected}><Plus className="h-4 w-4 mr-1" />{t("admin.newMatch")}</Button>
      </div>

      {editing && !editing.id && (
        <Card className="mb-4 border-primary/40">
          <CardHeader><CardTitle className="text-base">{t("admin.newMatch")}</CardTitle></CardHeader>
          {renderEditForm()}
        </Card>
      )}

      <div className="space-y-2">
        {filtered.map((m: Match) => {
          const isEditing = editing?.id === m.id;
          return (
            <Card key={m.id} className={isEditing ? "border-primary/40" : ""}>
              <CardContent className="flex items-center gap-2 p-3 text-sm">
                <Badge variant="outline" className="shrink-0">{phaseLabels[m.phase] ?? m.phase}{m.group_name ? ` ${m.group_name}` : ""}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{m.home_team} – {m.away_team}</div>
                  <div className="text-xs text-muted-foreground">{formatKickoff(m.kickoff_at)}</div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setEditing(isEditing ? null : { ...m, kickoff_at: toLocalInput(m.kickoff_at) })}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" onClick={() => remove(m.id)}><Trash2 className="h-4 w-4" /></Button>
              </CardContent>
              {isEditing && renderEditForm()}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

const ResultsSection = ({ tournaments, matches, selected, setSelected, onChange }: any) => {
  const { t } = useTranslation();
  const [skipEmails, setSkipEmails] = useState(true);
  const filtered = useMemo(() => matches.filter((m: Match) => m.tournament_id === selected), [matches, selected]);

  const Row = ({ m }: { m: Match }) => {
    const [h, setH] = useState<string>(m.home_score?.toString() ?? "");
    const [a, setA] = useState<string>(m.away_score?.toString() ?? "");
    const save = async () => {
      const ph = h === "" ? null : parseInt(h);
      const pa = a === "" ? null : parseInt(a);
      const status = ph !== null && pa !== null ? "finished" : "scheduled";
      const { error } = await supabase.from("matches").update({ home_score: ph, away_score: pa, status, suppress_result_email: skipEmails }).eq("id", m.id);
      if (error) toast.error(error.message); else { toast.success(t("league.resultSaved")); onChange(); }
    };
    return (
      <div className="flex items-center gap-2 py-1 text-sm">
        <div className="flex-1 text-right truncate text-xs">{m.home_team}</div>
        <Input className="w-12 h-9 px-1 text-center" type="number" inputMode="numeric" min={0} value={h} onChange={e => setH(e.target.value)} />
        <span>:</span>
        <Input className="w-12 h-9 px-1 text-center" type="number" inputMode="numeric" min={0} value={a} onChange={e => setA(e.target.value)} />
        <div className="flex-1 truncate text-xs">{m.away_team}</div>
        <Button size="sm" variant="outline" className="h-9" onClick={save}>{t("common.save")}</Button>
      </div>
    );
  };

  return (
    <div>
      <TournamentPicker tournaments={tournaments} value={selected} onChange={setSelected} />
      <div className="flex items-center justify-end gap-2 mb-3">
        <Label htmlFor="skip-emails" className="text-sm text-muted-foreground cursor-pointer">{t("admin.skipResultEmails")}</Label>
        <Switch id="skip-emails" checked={skipEmails} onCheckedChange={setSkipEmails} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("league.matchResults")}</CardTitle>
          <CardDescription>{t("league.matchResultsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">{t("admin.noMatches")}</p>}
          {filtered.map((m: Match) => <Row key={m.id} m={m} />)}
        </CardContent>
      </Card>
    </div>
  );
};

interface PreviewData {
  tournament_id: string;
  match_number: number | null;
  phase: string;
  group_name: string | null;
  kickoff_at: string;
  stadium: string | null;
  city: string | null;
  home_team: string;
  away_team: string;
  home_score: number | null;
  away_score: number | null;
  status: string;
  fifa_match_id: string | null;
  source_url: string | null;
}
interface PreviewRow {
  ok: boolean;
  errors: string[];
  warnings: string[];
  data: PreviewData | null;
  raw: Record<string, string>;
}

const ImportSection = ({ tournaments, selected, setSelected, onChange }: any) => {
  const { isSuperAdmin } = useAuth();
  const { t } = useTranslation();
  const [fileName, setFileName] = useState("");
  const [rawRows, setRawRows] = useState<Record<string, string>[]>([]);
  const [preview, setPreview] = useState<PreviewRow[]>([]);

  const handleFile = async (file: File) => {
    try {
      const rows = await parseFixturesFile(file);
      setFileName(file.name);
      setRawRows(rows);
      buildPreview(rows);
    } catch (e: any) {
      toast.error(e?.message || "Parse error");
    }
  };

  const buildPreview = (rows: Record<string, string>[]) => {
    const seenNumbers = new Set<string>();
    const seenKey = new Set<string>();
    const out: PreviewRow[] = rows.map(r => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Hard required fields
      for (const c of REQUIRED_COLS) if (!r[c]) errors.push(c);

      const phase = normalizePhase(r.phase || "");
      if (phase && !VALID_PHASES.includes(phase)) errors.push("phase");

      // Recommended fields → warnings only (group_name only relevant for group phase)
      for (const c of RECOMMENDED_COLS) {
        if (r[c]) continue;
        if (c === "group_name" && phase !== "group") continue;
        warnings.push(c);
      }

      const rawStatus = (r.status || "").trim();
      const status = normalizeStatus(rawStatus);
      if (!VALID_STATUSES.includes(status)) errors.push("status");
      if (!rawStatus) warnings.push("status=scheduled");

      const date = r.kickoff_at ? new Date(r.kickoff_at) : null;
      if (!date || isNaN(date.getTime())) errors.push("kickoff_at");

      const num = r.match_number ? parseInt(r.match_number) : NaN;
      if (r.match_number && (isNaN(num) || num <= 0)) errors.push("match_number");
      if (!isNaN(num)) {
        const key = String(num);
        if (seenNumbers.has(key)) errors.push("dup_match_number");
        seenNumbers.add(key);
      }

      // group_name is required for group-phase matches
      if (phase === "group" && !r.group_name) {
        errors.push("group_name");
      }

      const teamKey = `${(r.home_team||"").toLowerCase()}|${(r.away_team||"").toLowerCase()}|${r.kickoff_at}`;
      if (seenKey.has(teamKey)) errors.push("dup");
      seenKey.add(teamKey);

      if (!selected) errors.push("tournament");
      const ok = errors.length === 0;
      return {
        ok, errors, warnings, raw: r,
        data: ok ? {
          tournament_id: selected,
          match_number: isNaN(num) ? null : num,
          phase,
          group_name: phase === "group" ? (r.group_name || null) : null,
          kickoff_at: new Date(r.kickoff_at).toISOString(),
          stadium: r.stadium || null,
          city: r.city || null,
          home_team: r.home_team.trim(),
          away_team: r.away_team.trim(),
          home_score: r.home_score ? parseInt(r.home_score) : null,
          away_score: r.away_score ? parseInt(r.away_score) : null,
          status,
          fifa_match_id: r.fifa_match_id || null,
          source_url: r.source_url || null,
        } : null,
      };
    });
    setPreview(out);
  };

  const downloadTemplate = () => {
    const blob = new Blob([buildCsvTemplate()], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "tipovacka-fixtures-template.csv";
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const confirmImport = async () => {
    const valid = preview.filter(p => p.ok && p.data).map(p => p.data!);
    if (valid.length === 0) return toast.error(t("admin.noValidRows"));
    const { error } = await supabase.from("matches").insert(valid as any);
    if (error) toast.error(error.message);
    else {
      toast.success(`${valid.length} ${t("admin.imported")}`);
      setPreview([]); setRawRows([]); setFileName("");
      onChange();
    }
  };

  const clearTournament = async () => {
    if (!isSuperAdmin) return;
    if (!selected) return toast.error(t("admin.pickTournament"));
    const tr = tournaments.find((x: Tournament) => x.id === selected);
    const label = tr ? `${tr.name} · ${tr.year}` : "";
    if (!confirm(t("admin.confirmClearTournament", { name: label }))) return;
    const { error, count } = await supabase
      .from("matches")
      .delete({ count: "exact" })
      .eq("tournament_id", selected);
    if (error) toast.error(error.message);
    else {
      toast.success(`${count ?? 0} ${t("admin.matchesDeleted")}`);
      setPreview([]); setRawRows([]); setFileName("");
      onChange();
    }
  };

  return (
    <div>
      <TournamentPicker tournaments={tournaments} value={selected} onChange={setSelected} />
      {isSuperAdmin && (
        <Card className="mb-4 border-destructive/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t("admin.clearTournamentTitle")}</CardTitle>
            <CardDescription>{t("admin.clearTournamentDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              size="sm"
              disabled={!selected}
              onClick={clearTournament}
            >
              <Trash2 className="h-4 w-4 mr-1" />{t("admin.clearTournament")}
            </Button>
          </CardContent>
        </Card>
      )}
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">{t("admin.importTitle")}</CardTitle>
          <CardDescription>{t("admin.importDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-1" />{t("admin.downloadTemplate")}
            </Button>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">{t("admin.requiredCols")}</Label>
            <p className="text-[11px] font-mono break-all leading-relaxed">{REQUIRED_COLS.join(", ")}</p>
            <Label className="text-xs text-muted-foreground pt-1">{t("admin.recommendedCols")}</Label>
            <p className="text-[11px] font-mono break-all leading-relaxed text-amber-700 dark:text-amber-400">{RECOMMENDED_COLS.join(", ")}</p>
            <Label className="text-xs text-muted-foreground pt-1">{t("admin.optionalCols")}</Label>
            <p className="text-[11px] font-mono break-all leading-relaxed">{OPTIONAL_COLS.join(", ")}</p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">{t("admin.uploadFile")}</Label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:py-2 file:px-3"
            />
            {fileName && <p className="text-xs text-muted-foreground truncate">{fileName} · {rawRows.length} {t("league.matchesCount").toLowerCase()}</p>}
            {rawRows.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => buildPreview(rawRows)}>
                <Upload className="h-4 w-4 mr-1" />{t("admin.rebuildPreview")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {preview.length > 0 && (() => {
        const okCount = preview.filter(p => p.ok).length;
        const warnCount = preview.filter(p => p.ok && p.warnings.length > 0).length;
        const errCount = preview.length - okCount;
        return (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("admin.preview")} ({okCount}/{preview.length})
            </CardTitle>
            <CardDescription className="text-xs flex flex-wrap gap-2 pt-1">
              <Badge variant="outline" className="text-[10px]">OK: {okCount - warnCount}</Badge>
              {warnCount > 0 && <Badge className="text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/15">⚠ {warnCount}</Badge>}
              {errCount > 0 && <Badge variant="destructive" className="text-[10px]">✕ {errCount}</Badge>}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-1 text-xs">
            <div className="max-h-[55vh] overflow-y-auto space-y-1">
              {preview.map((p, i) => {
                const warnOnly = p.ok && p.warnings.length > 0;
                return (
                <div key={i} className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 p-2 rounded border ${
                  !p.ok ? "border-destructive/40 bg-destructive/5"
                  : warnOnly ? "border-amber-500/40 bg-amber-500/5"
                  : "border-border"
                }`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="w-10 text-muted-foreground shrink-0">#{p.raw.match_number || "?"}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{p.raw.home_team || "?"} – {p.raw.away_team || "?"}</div>
                      <div className="truncate text-muted-foreground text-[10px]">
                        {p.raw.phase}{p.raw.group_name ? ` ${p.raw.group_name}` : ""} · {p.raw.kickoff_at}
                        {p.raw.stadium ? ` · ${p.raw.stadium}` : ""}{p.raw.city ? `, ${p.raw.city}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 shrink-0">
                    {!p.ok && (
                      <Badge variant="destructive" className="text-[10px] whitespace-normal text-left">
                        {p.errors.join(", ")}
                      </Badge>
                    )}
                    {p.warnings.length > 0 && (
                      <Badge className="text-[10px] whitespace-normal text-left bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/40 hover:bg-amber-500/15">
                        ⚠ {p.warnings.join(", ")}
                      </Badge>
                    )}
                    {p.ok && p.warnings.length === 0 && (
                      <Badge variant="outline" className="text-[10px]">OK</Badge>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            <div className="pt-3 flex gap-2">
              <Button onClick={confirmImport} className="bg-gradient-pitch">{t("admin.confirmImport")}</Button>
              <Button variant="outline" onClick={() => { setPreview([]); setRawRows([]); setFileName(""); }}>{t("common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
        );
      })()}
    </div>
  );
};

const GroupResultsSection = ({ tournaments, matches, selected, setSelected }: {
  tournaments: Tournament[]; matches: Match[]; selected: string; setSelected: (v: string) => void;
}) => {
  const [results, setResults] = useState<Array<{ id?: string; group_name: string; first_team: string; second_team: string }>>([]);
  const [loading, setLoading] = useState(false);

  const groups = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    matches.filter(m => m.tournament_id === selected && m.phase === "group" && m.group_name).forEach((m) => {
      if (!map[m.group_name!]) map[m.group_name!] = new Set();
      map[m.group_name!].add(m.home_team);
      map[m.group_name!].add(m.away_team);
    });
    return Object.entries(map).map(([g, teams]) => ({ group: g, teams: Array.from(teams).sort() })).sort((a, b) => a.group.localeCompare(b.group));
  }, [matches, selected]);

  const load = async () => {
    if (!selected) return;
    setLoading(true);
    const { data } = await supabase.from("group_results").select("*").eq("tournament_id", selected);
    const map: Record<string, any> = {};
    (data ?? []).forEach((r: any) => { map[r.group_name] = r; });
    setResults(groups.map((g) => ({
      id: map[g.group]?.id,
      group_name: g.group,
      first_team: map[g.group]?.first_team ?? "",
      second_team: map[g.group]?.second_team ?? "",
    })));
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selected, matches]);

  const save = async (row: typeof results[number]) => {
    if (!row.first_team || !row.second_team) { toast.error("Vyber oba týmy"); return; }
    if (row.first_team === row.second_team) { toast.error("Týmy musí být různé"); return; }
    const payload = { tournament_id: selected, group_name: row.group_name, first_team: row.first_team, second_team: row.second_team };
    const { error } = row.id
      ? await supabase.from("group_results").update(payload).eq("id", row.id)
      : await supabase.from("group_results").insert(payload);
    if (error) toast.error(error.message); else { toast.success("Uloženo"); load(); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Výsledky skupin</CardTitle>
        <CardDescription>Oficiální postupující ze skupin. Po uložení se přepočtou body všech tipů.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Turnaj</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-10 max-w-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{tournaments.map((tr) => <SelectItem key={tr.id} value={tr.id}>{tr.name} · {tr.year}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {loading ? <p className="text-sm text-muted-foreground">Načítám…</p> :
         groups.length === 0 ? <p className="text-sm text-muted-foreground">Žádné skupiny pro tento turnaj.</p> : (
          <div className="grid gap-3 sm:grid-cols-2">
            {results.map((row) => {
              const teams = groups.find(g => g.group === row.group_name)?.teams ?? [];
              return (
                <Card key={row.group_name} className="border-border">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Skupina {row.group_name}</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">1. místo</Label>
                      <Select value={row.first_team} onValueChange={(v) => setResults(r => r.map(x => x.group_name === row.group_name ? { ...x, first_team: v } : x))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Vyber tým" /></SelectTrigger>
                        <SelectContent>{teams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">2. místo</Label>
                      <Select value={row.second_team} onValueChange={(v) => setResults(r => r.map(x => x.group_name === row.group_name ? { ...x, second_team: v } : x))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Vyber tým" /></SelectTrigger>
                        <SelectContent>{teams.filter(t => t !== row.first_team).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" onClick={() => save(row)} className="rounded-full">Uložit</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminPage;

